/**
 * Anwendungshülle: Kopfzeile, Inhalt, Navigation.
 *
 * `100dvh` statt `100vh` (ADR 0001): Bei `vh` rechnet der mobile Browser die
 * ein- und ausfahrende Adressleiste nicht mit, wodurch die untere Navigation
 * hinter den Bildschirmrand rutscht.
 *
 * Der Inhaltsbereich scrollt, nicht die Seite. Nur so bleiben Kopfzeile und
 * Navigation stehen — und nur so lässt sich am unteren Rand verlässlich Platz für
 * den Geräte-Sicherheitsabstand halten.
 */
import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { useLogout } from "@/api/auth";
import { SyncIndicator } from "@/components/SyncIndicator";
import { Button } from "@/components/ui/Button";
import { Sheet } from "@/components/ui/Sheet";
import { t } from "@/i18n";
import { cn } from "@/lib/cn";

import { BottomNav } from "./BottomNav";
import { DESKTOP_NAV } from "./navigation";
import { SideNav } from "./SideNav";

export interface AppShellProps {
  /** Wird statt `<Outlet />` gezeigt — für Tests und Sonderfälle. */
  children?: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const logout = useLogout();
  const navigate = useNavigate();

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-surface">
      <SideNav />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Sprungmarke: Wer per Tastatur bedient, soll nicht jedes Mal durch die
            gesamte Navigation tabben müssen (Kapitel 44). */}
        <a
          href="#inhalt"
          className={cn(
            "sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50",
            "focus:rounded-card focus:bg-ink focus:px-3 focus:py-2 focus:text-ink-inverted",
          )}
        >
          {t.common.skipToContent}
        </a>

        <header
          className={cn(
            "flex shrink-0 items-center gap-2 border-b border-line bg-surface-raised px-3",
            "pt-safe-t md:px-5",
          )}
        >
          <div className="flex min-h-touch items-center gap-2 md:hidden">
            <img src="/icon.svg" alt="" width={24} height={24} className="rounded" />
            <span className="text-[1rem] font-semibold tracking-tight text-ink">{t.app.name}</span>
          </div>
          <div className="ml-auto flex items-center gap-1">
            <SyncIndicator />
            {/* Abmelden aus jeder Ansicht heraus. Auf der Kontoseite steht die
                ausführliche Fassung samt Erklärung; hier genügt der kurze Weg —
                wer das Gerät weggibt, sucht ihn nicht erst im Menü. */}
            <Button
              variant="ghost"
              disabled={logout.isPending}
              onClick={() =>
                logout.mutate(undefined, {
                  // `onSettled` statt `onSuccess`: Der Haken räumt örtlich auch
                  // dann auf, wenn der Server nicht erreichbar war — auf einem
                  // verlorenen Gerät ist genau das der wichtige Fall.
                  onSettled: () => void navigate({ to: "/login", replace: true }),
                })
              }
            >
              {t.account.logoutShort}
            </Button>
          </div>
        </header>

        <main id="inhalt" className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {children ?? <Outlet />}
        </main>

        <BottomNav onOpenMore={() => setMoreOpen(true)} />
      </div>

      {/* „Mehr": auf dem Telefon der Zugang zu den übrigen Bereichen aus
          Kapitel 38, die nicht in sieben Plätze passen. */}
      <Sheet
        open={moreOpen}
        onOpenChange={setMoreOpen}
        title={t.common.more}
        snapPoints={[0.75, 0.95]}
      >
        <div className="flex flex-col gap-5">
          {DESKTOP_NAV.map((group) => (
            <div key={group.label}>
              <p className="section-label pb-2">{group.label}</p>
              {/* Jeder Bereich ist ein Verweis: `DESKTOP_NAV` führt nur
                  Einträge mit Ziel (`NavTarget`). Hier stand daneben ein
                  zweiter Zweig für Bereiche ohne Ansicht — er war seit dem
                  letzten Ausbau unerreichbar und hat vor allem verdeckt, dass
                  alle vierzehn Bereiche stehen. */}
              <ul className="flex flex-col gap-1">
                {group.entries.map((entry) => (
                  <li key={entry.id}>
                    <Link
                      to={entry.to}
                      onClick={() => setMoreOpen(false)}
                      className={cn(
                        "flex min-h-touch w-full items-center gap-3 rounded-card px-2 text-left",
                        "text-[1rem] text-ink hover:bg-surface-sunken",
                        "data-[status=active]:bg-surface-sunken data-[status=active]:font-semibold",
                      )}
                    >
                      <entry.icon className="size-5 shrink-0" />
                      <span className="truncate">{entry.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Sheet>
    </div>
  );
}
